import { Descriptions, Modal, Tag, Timeline, Tooltip } from 'antd';
import { useState } from 'react';
import jobsService from '../../services/jobs.service';

import dayjs from 'dayjs';
import { JobOfferStatus } from '../../types/offer';
import { notifyError, notifySuccess } from '../../utils/notification';
import { formatDashedNumber } from '../../utils/strings';
import { PDFViewer } from '../pdf-viewer-modal/pdf-viewer-modal';
import { JobOfferModalForm } from './forms/job-offer-modal-form';
import { ButtonType, TAButton } from '../widgets';

export default function JobOfferViewModal({
	visible,
	offer,
	job,
	company,
	onError,
	onAccept,
	onToggleOffer,
	onCancel,
	onWithdraw,
	onCounter,
	onReject,
	backOffice,
	disableButtons
}: any) {
	const [modalVisible, showModal] = useState(false);
	const [modalUpdateVisible, showUpdateModal] = useState(false);
	const [selectedOffer, setSelectedOffer] = useState(null);
	const [inputId, showInput] = useState(null);
	const [inputValue, showInputValue] = useState(null);
	const [pdfViewerState, setPDFViewerState] = useState({ doc: undefined, visible: false });
	const counterOffers = offer.counterOffers;
	const onOfferWithdraw = async () => {
		try {
			await jobsService.withdrawOffer(offer.job?.id || job.id, offer.id || offer._id);
		} catch (e) {
			notifyError(`Error while trying to withdraw the offer`);
			console.error(e);
			onError && (await onError());
			return;
		}
		notifySuccess('Offer is withdrawn successfully!');
		onWithdraw && (await onWithdraw());
	};

	const onOfferAccept = async () => {
		try {
			await jobsService.acceptOffer(offer.job?.id || job.id, offer.id || offer._id);
		} catch (e) {
			notifyError(`Error while trying to Accept the offer`);
			console.error(e);
			onError && (await onError());
			return;
		}
		notifySuccess('Offer is accepted successfully!');
		onAccept && (await onAccept());
	};

	onToggleOffer = async () => {
		try {
			let resOffer: any = null;
			resOffer = await jobsService.toggle_offer(offer.id || offer._id);
			if (!resOffer) return null;
			notifySuccess('Offer is updated successfully!');
		} catch (e: any) {
			notifyError(e.message);
		}
	};
	const onOfferReject = async () => {
		try {
			await jobsService.rejectOffer(offer.job?.id || job.id, offer.id || offer._id);
		} catch (e) {
			notifyError(`Error while trying to Reject the offer`);
			console.error(e);
			onError && (await onError());
			return;
		}
		notifySuccess('Offer is rejected successfully!');
		onReject && (await onReject());
	};

	const updateOfferCounter = async counterOffer => {
		try {
			const fd = new FormData();
			Object.keys(counterOffer).forEach(k => counterOffer[k] && fd.append(k, counterOffer[k]));
			const res = await jobsService.updateOfferCounter(job.id, offer.id || offer._id, fd);
			if (!res) throw new Error(`Error while trying to update the offer`);
			notifySuccess('Offer is updated successfully!');
			window.location.reload(); // Temporary measure, we should use redux to manage the state because the callback is designed for static data only
		} catch (e: any) {
			notifyError(e.message);
			return;
		}
		showUpdateModal(false);
		onCounter({ ...counterOffer, who: backOffice ? 'back_office' : 'company' });
	};

	const onOfferCounter = async counterOffer => {
		try {
			const fd = new FormData();
			Object.keys(counterOffer).forEach(k => counterOffer[k] && fd.append(k, counterOffer[k]));
			const res = await jobsService.counterOffer(job.id, offer.id || offer._id, fd);
			if (!res) throw new Error(`Error while trying to counter the offer`);
		} catch (e: any) {
			notifyError(e.message);
			return;
		}
		showModal(false);
		onCounter({ ...counterOffer, who: backOffice ? 'back_office' : 'company' });
	};

	const onAttachmentView = e => {
		e.preventDefault();
		setPDFViewerState({ doc: e.target.href, visible: true });
	};
	const lastCounterOffer = counterOffers[counterOffers.length - 1];
	const lastCounterActor = backOffice ? ['back_office', 'developer'] : ['company'];
	const disabled = backOffice
		? counterOffers.length > 0 && lastCounterActor.includes(lastCounterOffer.who)
		: !counterOffers.length || (counterOffers.length > 0 && lastCounterActor.includes(lastCounterOffer.who));

	const backOfficeOfferButtonsMapper = {
		[JobOfferStatus.ACCEPTED]: [
			<TAButton
				btntype={ButtonType.DANGER}
				onClick={async () => {
					onToggleOffer();
					onReject && (await onReject());
				}}
			>
				Cancel Offer
			</TAButton>
		],
		[JobOfferStatus.PENDING]: [
			<TAButton btntype={ButtonType.PRIMARY} className="mb-1" onClick={onOfferAccept} disabled={disabled}>
				Accept
			</TAButton>,
			<TAButton btntype={ButtonType.DANGER} className="mb-1" onClick={onOfferReject} disabled={disabled}>
				Reject
			</TAButton>,
			<TAButton btntype={ButtonType.PRIMARY} onClick={() => showModal(true)} disabled={disabled}>
				Counter
			</TAButton>
		],
		[JobOfferStatus.REJECTED]: [
			<TAButton
				onClick={async () => {
					onToggleOffer();
					onAccept && (await onAccept());
				}}
				btntype={ButtonType.PRIMARY}
			>
				Accept Offer
			</TAButton>
		],
		[JobOfferStatus.WITHDRAWN]: [
			<TAButton
				onClick={async () => {
					onToggleOffer();
					onAccept && (await onAccept());
				}}
				btntype={ButtonType.PRIMARY}
			>
				Accept Offer
			</TAButton>
		]
	};
	const companyOfferButtonsMapper = {
		[JobOfferStatus.ACCEPTED]: [],
		[JobOfferStatus.PENDING]: [
			<TAButton btntype={ButtonType.PRIMARY} className="mb-1" onClick={onOfferAccept} disabled={disabled}>
				Accept
			</TAButton>,
			<TAButton btntype={ButtonType.DANGER} className="mb-1" onClick={onOfferWithdraw}>
				Withdraw
			</TAButton>,
			<TAButton btntype={ButtonType.PRIMARY} onClick={() => showModal(true)} disabled={disabled}>
				Counter
			</TAButton>
		],
		[JobOfferStatus.REJECTED]: [],
		[JobOfferStatus.WITHDRAWN]: []
	};

	const buttons = backOffice ? backOfficeOfferButtonsMapper[offer.status] : companyOfferButtonsMapper[offer.status];
	const lastOffer = counterOffers.length > 0 ? counterOffers[counterOffers.length - 1] : offer;
	return (
		<Modal
			title={`Job Offer to ${(offer.candidate && `${offer.candidate.code}`) || ''} for ${job.title} vacancy`}
			visible={visible}
			footer={
				!disableButtons ? (
					<div className="d-flex  justify-content-end">{buttons}</div>
				) : (
					[
						<TAButton btntype={ButtonType.GHOST} onClick={onCancel}>
							Cancel
						</TAButton>
					]
				)
			}
			onCancel={onCancel}
		>
			<div className="counter-offers">
				<Timeline mode="left">
					<Timeline.Item>
						<div className="d-flex justify-content-between">
							<Tooltip title={dayjs(offer.createdAt).fromNow()}>
								{dayjs(offer.createdAt).format('ddd, MMM D, YYYY h:mm A')}
							</Tooltip>
							{counterOffers.length === 0 && offer.status === 'Accepted' && (
								<Tooltip title="This is the final accepted offer agreement">
									<Tag color="geekblue">Accepted</Tag>
								</Tooltip>
							)}
						</div>
						<Descriptions layout="horizontal" column={{ xs: 1, md: 2 }} className="my-2" size="small">
							{offer.jobType ? (
								<Descriptions.Item label="Job Type">
									{formatDashedNumber(offer.jobType)}
								</Descriptions.Item>
							) : null}
							<Descriptions.Item label="Join Date">
								{offer.startingDate ? dayjs(offer.startingDate).format('DD/MM/YYYY') : 'Unspecified'}
							</Descriptions.Item>
							<Descriptions.Item label="Attachment">
								{(offer.attachment && (
									<a href={offer.attachment} onClick={onAttachmentView}>
										View
									</a>
								)) ||
									'NA'}
							</Descriptions.Item>
							<Descriptions.Item label="Created by">company</Descriptions.Item>
							<Descriptions.Item label="Notes" span={2}>
								{offer.notes || 'NA'}
							</Descriptions.Item>
							<Descriptions.Item label="Salary" span={2}>
								{formatDashedNumber(offer.salary)} {offer.currency ?? null}
							</Descriptions.Item>
						</Descriptions>
					</Timeline.Item>
					{counterOffers.map((offer, indx) => (
						<Timeline.Item
							color={
								counterOffers.length - 1 === indx && offer.status === 'Accepted'
									? 'green'
									: offer.who == 'back_office'
									? 'red'
									: 'blue'
							}
						>
							<div className="d-flex justify-content-between">
								<Tooltip title={dayjs(offer.createdAt).fromNow()}>
									{dayjs(offer.createdAt).format('ddd, MMM D, YYYY h:mm A')}
								</Tooltip>
								{counterOffers.length - 1 === indx && offer.status === 'Accepted' && (
									<Tooltip title="This is the final accepted offer agreement">
										<Tag color="geekblue">Accepted</Tag>
									</Tooltip>
								)}
							</div>

							<Descriptions layout="horizontal" column={{ xs: 1, md: 2 }} className="my-2" size="small">
								<Descriptions.Item label="Join Date">
									{offer.startingDate
										? dayjs(offer.startingDate).format('DD/MM/YYYY')
										: 'Unspecified'}
								</Descriptions.Item>
								<Descriptions.Item label="Attachment">
									{(offer.attachment && (
										<a href={offer.attachment} onClick={onAttachmentView}>
											View
										</a>
									)) ||
										'NA'}
								</Descriptions.Item>
								{backOffice ? (
									<Descriptions.Item label="Created by">
										{(offer.who && offer.who.replace('_', ' ')) || 'NA'}
									</Descriptions.Item>
								) : null}
								<Descriptions.Item label="Notes" span={2}>
									{offer.notes || 'NA'}
								</Descriptions.Item>
								<Descriptions.Item label="Salary" span={2}>
									{formatDashedNumber(offer.salary)} {offer.currency ?? null}
								</Descriptions.Item>
							</Descriptions>
						</Timeline.Item>
					))}
				</Timeline>
			</div>

			{pdfViewerState.visible && pdfViewerState.doc && (
				<PDFViewer
					url={pdfViewerState.doc}
					title={`${offer.candidate.code}'s offer`}
					visible={pdfViewerState.visible}
					onCancel={() => setPDFViewerState({ doc: undefined, visible: false })}
					downloadable
				/>
			)}
			{modalVisible && (
				<JobOfferModalForm
					visible={modalVisible}
					headerTitle={`Counter offer to candiadte ${offer.candidate.firstName} ${
						offer.candidate.lastName
					} for ${job.title} vacancy at ${offer.company.name || company.name}`}
					onOk={onOfferCounter}
					onCancel={() => {
						showModal(false);
					}}
					values={{
						salary: lastOffer.salary,
						currency: lastOffer.currency,
						jobType: lastOffer.jobType,
						startingDate: lastOffer.startingDate ? dayjs(lastOffer.startingDate) : undefined
					}}
				/>
			)}
		</Modal>
	);
}
